import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  CheckCircle, 
  Users, 
  Calendar, 
  Stethoscope, 
  Heart, 
  Sparkles,
  MessageCircle,
  Target,
  TrendingUp,
  Phone,
  FileText,
  ArrowRight,
  Crown,
  Star
} from 'lucide-react';

const PackagesPage = () => {
  const location = useLocation();

  // Handle scroll to fertility package when hash is present
  useEffect(() => {
    if (location.hash === '#fertility-package') {
      setTimeout(() => {
        const element = document.getElementById('fertility-package');
        if (element) {
          const headerOffset = 100;
          const elementTop = element.offsetTop - headerOffset;
          window.scrollTo({
            top: elementTop,
            behavior: 'smooth'
          });
        }
      }, 300);
    }
  }, [location]);

  const packages = [
    {
      id: 1,
      name: 'Standard Package',
      tagline: 'Fit & Balanced',
      gradient: 'from-orange-500 to-amber-600',  // Train = Orange
      accentColor: 'orange',
      iconBg: 'bg-orange-500/10',
      iconColor: 'text-orange-600',
      borderGlow: 'hover:shadow-orange-500/20',
      whoFor: 'Anyone starting their fitness journey and seeking guidance from a certified nutritionist and fitness coach.',
      includes: [
        'Personalized diet plans and workouts designed by a certified nutritionist',
        'Weekly call check by nutritionist, if required a consultation by Doctor',
        'Monthly medical oversight by Medical Lead, Dr. Pooja Sree (nutrition & progress review)'
      ],
      durations: ['12 weeks', '25 weeks'],
      pricing: ['Individual', 'Partner']
    },
    {
      id: 2,
      name: 'Premium Health Package',
      tagline: 'Vitality Plus',
      gradient: 'from-blue-600 to-cyan-700',  // Treat = Blue
      accentColor: 'blue',
      iconBg: 'bg-blue-500/10',
      iconColor: 'text-blue-600',
      borderGlow: 'hover:shadow-blue-500/20',
      popular: true,
      whoFor: 'Those wanting full medical supervision along with advanced fitness and nutrition guidance.',
      includes: [
        'Personalized diet plans and workouts designed by Dr. Pooja Sree',
        'Weekly consultation with Medical Lead, Dr. Pooja Sree (MBBS, DNB General Medicine)',
        'Continuous monitoring of diets, progress, and reports by Medical Lead, Dr. Pooja Sree'
      ],
      durations: ['12 weeks', '25 weeks'],
      pricing: ['Individual', 'Partner']
    },
    {
      id: 3,
      name: "Women's Wellness Package",
      tagline: 'Her Health Connect',
      gradient: 'from-emerald-500 to-green-600',  // Transform = Green
      accentColor: 'emerald',
      iconBg: 'bg-emerald-500/10',
      iconColor: 'text-emerald-600',
      borderGlow: 'hover:shadow-emerald-500/20',
      whoFor: 'Women focusing on complete health, including reproductive and overall wellness.',
      includes: [
        'All benefits of the Premium Health Package',
        'Monthly consultation with our in-house Gynaecologist',
        'Full monitoring of progress, diet, and medical reports by Medical Lead, Dr. Pooja Sree'
      ],
      durations: ['12 weeks', '25 weeks'],
      pricing: ['Individual', 'Partner']
    },
    {
      id: 4,
      name: 'Hope & Harmony Fertility Plan',
      tagline: 'Fertility Support',
      gradient: 'from-pink-500 to-rose-600',
      accentColor: 'pink',
      iconBg: 'bg-pink-500/10',
      iconColor: 'text-pink-600',
      borderGlow: 'hover:shadow-pink-500/20',
      isNew: true,
      whoFor: 'Individuals or couples planning for pregnancy, experiencing difficulty conceiving, or those who have not been expecting a child despite trying.',
      includes: [
        'Comprehensive Fertility Health Assessment (Hormonal profile, reproductive health review)',
        'Monthly consultation with our In-House Fertility Specialist',
        'Personalized fertility nutrition & lifestyle guidance',
        'Ovulation tracking & cycle monitoring support',
        'Male fertility assessment guidance (for partner plans)',
        'Continuous monitoring of reports and progress by Fertility Specialist'
      ],
      durations: ['25 weeks', '52 weeks'],
      pricing: ['Individual', 'Couple']
    }
  ];

  const whatYouGet = [
    {
      icon: <Target className="w-6 h-6" />,
      title: 'Personalized Programs Just for You',
      description: 'Each plan is tailored to your goals, lifestyle, and preferences. Your coach and nutritionist design workout routines, meal plans, and progress strategies specifically for you.'
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: 'Guided Progress & Accountability',
      description: 'Your coach monitors your journey closely with regular check ins, providing insights, adjustments, and motivation for consistent results.'
    },
    {
      icon: <MessageCircle className="w-6 h-6" />,
      title: 'Stay Connected With Your Coach',
      description: 'Regular calls to discuss progress, plus extra calls if needed. Messages can be sent anytime (except Sundays), and your plan continues uninterrupted.'
    },
    {
      icon: <Stethoscope className="w-6 h-6" />,
      title: 'Medical Oversight for Safe Progress',
      description: 'Medical Lead, Dr. Pooja Sree supervises all plans, reviewing your diet, progress, and health reports to ensure safe and effective progress.'
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: 'Health Insights',
      description: 'Share your latest blood test reports for more precise plan adjustments and tracking.'
    }
  ];

  const howItWorks = [
    {
      step: 1,
      title: 'Initial Consultation',
      description: 'Introductory call to understand your goals, preferences, and requirements.'
    },
    {
      step: 2,
      title: 'Share Your Details',
      description: 'Food preferences, schedule, medical history, and other relevant information.'
    },
    {
      step: 3,
      title: 'Customized Plan Creation',
      description: 'Coach evaluates your info and designs the ideal plan.'
    },
    {
      step: 4,
      title: 'Regular Check Ins & Adjustments',
      description: 'Track results, discuss progress, and tweak the plan as needed.'
    },
    {
      step: 5,
      title: 'Ongoing Support & Oversight',
      description: 'Continuous guidance from your coach plus medical supervision by Medical Lead, Dr. Pooja Sree.'
    },
    {
      step: 6,
      title: 'Achieve Your Goals',
      description: 'Follow your plan consistently and celebrate measurable results!'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      
      {/* Hero Section - Premium Dark */}
      <section className="pt-28 pb-20 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950"></div>
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-emerald-600/20 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600/20 to-cyan-600/20 border border-blue-500/30 rounded-full text-blue-300 text-sm font-medium mb-6 backdrop-blur-sm">
              <Crown className="w-4 h-4" />
              Premium Wellness Programs
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Transform Your Health with{' '}
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                Expert Care
              </span>
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed max-w-2xl mx-auto">
              All packages include personal fitness coaching, nutrition planning, and medical supervision by our{' '}
              <span className="text-white font-semibold">Medical Lead, Dr. Pooja Sree</span>. 
              Each package is available for individuals or for you + a partner.
            </p>
          </div>
        </div>
      </section>

      {/* Packages Section - Premium Cards */}
      <section className="py-20 relative" id="packages">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            {packages.map((pkg) => (
              <div 
                key={pkg.id}
                id={pkg.isNew ? 'fertility-package' : undefined}
                className={`relative bg-slate-900/50 backdrop-blur-sm rounded-3xl overflow-hidden border border-slate-700/50 transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl ${pkg.borderGlow}`}
              >
                {pkg.popular && (
                  <div className="absolute top-6 right-6 z-20">
                    <div className="flex items-center gap-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg">
                      <Star className="w-3 h-3 fill-current" />
                      MOST POPULAR
                    </div>
                  </div>
                )}
                {pkg.isNew && (
                  <div className="absolute top-6 right-6 z-20">
                    <div className="flex items-center gap-1 bg-gradient-to-r from-pink-500 to-rose-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg animate-pulse">
                      <Sparkles className="w-3 h-3" />
                      NEW
                    </div>
                  </div>
                )}
                
                {/* Header */}
                <div className={`relative bg-gradient-to-br ${pkg.gradient} p-8 overflow-hidden`}>
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
                  <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-12 -translate-x-12"></div>
                  <div className="relative z-10">
                    <p className="text-white/80 text-sm font-medium tracking-wider uppercase mb-2">{pkg.tagline}</p>
                    <h3 className="text-2xl font-bold text-white">{pkg.name}</h3>
                  </div>
                </div>

                {/* Content */}
                <div className="p-8">
                  {/* Who it's for */}
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`p-2 rounded-lg ${pkg.iconBg}`}>
                        <Users className={`w-4 h-4 ${pkg.iconColor}`} />
                      </div>
                      <h4 className="font-semibold text-white">Who it&apos;s for</h4>
                    </div>
                    <p className="text-sm text-slate-400 leading-relaxed">{pkg.whoFor}</p>
                  </div>

                  {/* Includes */}
                  <div className="mb-6">
                    <h4 className="font-semibold text-white mb-4">What&apos;s Included:</h4>
                    <ul className="space-y-3">
                      {pkg.includes.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <div className={`p-1 rounded-full ${pkg.iconBg} mt-0.5`}>
                            <CheckCircle className={`w-4 h-4 ${pkg.iconColor}`} />
                          </div>
                          <span className="text-sm text-slate-300">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Duration & Pricing */}
                  <div className="bg-slate-800/50 rounded-2xl p-5 space-y-3 mb-6 border border-slate-700/50">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${pkg.iconBg}`}>
                        <Calendar className={`w-4 h-4 ${pkg.iconColor}`} />
                      </div>
                      <div>
                        <span className="text-xs text-slate-500 uppercase tracking-wider">Duration</span>
                        <p className="text-white font-medium">{pkg.durations.join(' or ')}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${pkg.iconBg}`}>
                        <Heart className={`w-4 h-4 ${pkg.iconColor}`} />
                      </div>
                      <div>
                        <span className="text-xs text-slate-500 uppercase tracking-wider">Options</span>
                        <p className="text-white font-medium">{pkg.pricing.join(' / ')}</p>
                      </div>
                    </div>
                  </div>

                  {/* CTA */}
                  <Link
                    to="/submit-details"
                    className={`w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r ${pkg.gradient} text-white py-4 px-6 rounded-2xl font-semibold hover:opacity-90 transition-all duration-300 shadow-lg hover:shadow-xl`}
                  >
                    Get Started
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Evidence-based Nutrition Plans Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-950 via-slate-950 to-teal-950"></div>
        <div className="absolute top-1/2 left-0 w-72 h-72 bg-emerald-600/10 rounded-full blur-3xl -translate-y-1/2"></div>
        <div className="absolute top-1/2 right-0 w-72 h-72 bg-teal-600/10 rounded-full blur-3xl -translate-y-1/2"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-emerald-400 text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                Nutrition That Works
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Evidence Based Weekly Nutrition Plans
              </h2>
              <p className="text-lg text-slate-400">
                Tailored to your health conditions, preferences, and goals, with both vegetarian and non-vegetarian options.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {[
                { title: 'Customized for Health Conditions', desc: 'Diabetes, Thyroid, PMOS (previously called PCOS), Antenatal & Postpartum' },
                { title: 'Weekly Meal Plans', desc: 'Easy, everyday recipes you can actually follow' },
                { title: 'Veg & Non-Veg Options', desc: 'Both vegetarian and non-vegetarian choices available' },
                { title: 'Plans Adjusted Weekly', desc: 'Based on your progress and feedback' }
              ].map((item, idx) => (
                <div key={idx} className="bg-slate-900/50 backdrop-blur-sm rounded-2xl p-5 border border-slate-700/50 flex items-start gap-4">
                  <div className="p-2 rounded-xl bg-emerald-500/10">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-white">{item.title}</p>
                    <p className="text-sm text-slate-400">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-r from-emerald-900/50 to-teal-900/50 rounded-3xl p-8 border border-emerald-500/20 backdrop-blur-sm">
              <p className="text-center text-white text-lg leading-relaxed">
                <span className="font-semibold">No complicated diets. No exotic recipes.</span><br />
                <span className="text-slate-300">Just the foods you already eat at home, from your childhood favorites to your daily meals,</span>{' '}
                <span className="text-emerald-400 font-medium">balanced and tailored for your health.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Get Section */}
      <section className="py-20 relative">
        <div className="absolute inset-0 bg-slate-950"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              What You&apos;ll Get
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Every package is designed to give you comprehensive support for your health journey
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whatYouGet.map((item, idx) => (
              <div 
                key={idx}
                className="bg-slate-900/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 hover:border-blue-500/30 transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/10"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-2xl flex items-center justify-center text-blue-400 mb-4 border border-blue-500/20">
                  {item.icon}
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              How It Works
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Your journey to better health in 6 simple steps
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-6 top-0 bottom-0 w-px bg-gradient-to-b from-blue-600 via-cyan-600 to-blue-600 hidden md:block"></div>
              
              <div className="space-y-6">
                {howItWorks.map((step, idx) => (
                  <div key={idx} className="flex gap-6 items-start">
                    {/* Step number */}
                    <div className="relative z-10 w-12 h-12 bg-gradient-to-br from-blue-600 to-cyan-700 rounded-2xl flex items-center justify-center text-white font-bold flex-shrink-0 shadow-lg shadow-blue-500/30">
                      {step.step}
                    </div>
                    {/* Content */}
                    <div className="bg-slate-900/50 backdrop-blur-sm rounded-2xl p-5 border border-slate-700/50 flex-1 hover:border-blue-500/30 transition-colors">
                      <h3 className="font-semibold text-white mb-1">{step.title}</h3>
                      <p className="text-sm text-slate-400">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Option Banner */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-violet-900 via-cyan-900 to-violet-900"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=%2260%22 height=%2260%22 viewBox=%220 0 60 60%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cg fill=%22none%22 fill-rule=%22evenodd%22%3E%3Cg fill=%22%23ffffff%22 fill-opacity=%220.03%22%3E%3Cpath d=%22M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-50"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-5">
              <div className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-3xl flex items-center justify-center border border-white/20">
                <Sparkles className="w-10 h-10 text-amber-400" />
              </div>
              <div className="text-white">
                <h3 className="text-2xl md:text-3xl font-bold mb-2">Partner Option Available</h3>
                <p className="text-white/80 text-lg">Bring a sibling, spouse, or friend. Stay motivated together and reach your goals faster.</p>
              </div>
            </div>
            <Link
              to="/submit-details"
              className="bg-white text-violet-900 px-8 py-4 rounded-2xl font-semibold hover:bg-slate-100 transition-colors flex items-center gap-2 whitespace-nowrap shadow-xl"
            >
              Book Consultation
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative">
        <div className="absolute inset-0 bg-slate-950"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Start Your Journey?
            </h2>
            <p className="text-slate-400 mb-8">
              Book a free consultation to discuss which package is right for you
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/submit-details"
                className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-700 text-white px-8 py-4 rounded-2xl font-semibold hover:opacity-90 transition-all shadow-lg shadow-blue-500/30"
              >
                <Phone className="w-5 h-5" />
                Book Free Consultation
              </Link>
              <a
                href="mailto:support@tcubefitness.com"
                className="inline-flex items-center justify-center gap-2 border-2 border-slate-700 text-white px-8 py-4 rounded-2xl font-semibold hover:bg-slate-800 hover:border-slate-600 transition-all"
              >
                <MessageCircle className="w-5 h-5" />
                Ask a Question
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PackagesPage;
